import React, { useEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { t } from "i18next";

const CompanyLineChart = ({ chartData, chartHeight }) => {
  useEffect(() => {
    // Create root element
    const root = am5.Root.new("companyLineChartDiv");

    const myTheme = am5.Theme.new(root);

    myTheme.rule("AxisLabel", ["minor"]).setAll({
      dy: 1,
    });

    myTheme.rule("Grid", ["minor"]).setAll({
      strokeOpacity: 0.08,
    });

    root.setThemes([am5themes_Animated.new(root), myTheme]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        paddingLeft: 0,
      })
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      })
    );
    cursor.lineY.set("visible", false);

    const formattedData = chartData?.map((item) => ({
      date: new Date(item.date).getTime(),
      orders: item.orders,
      earnings: item.earnings,
    }));

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0,
        baseInterval: {
          timeUnit: "day",
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true,
          minGridDistance: 200,
          minorLabelsEnabled: true,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const yAxisOrders = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    const yAxisEarnings = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, { opposite: true }),
        extraMin: 0.1,
      })
    );

    const ordersSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("totalOrders"),
        xAxis: xAxis,
        yAxis: yAxisOrders,
        valueYField: "orders",
        valueXField: "date",
        stroke: am5.color("#FF5733"),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY} Appointments",
        }),
      })
    );

    ordersSeries.bullets.push(() => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: ordersSeries.get("fill"),
      });
      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    const earningsSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("totalEarnings"),
        xAxis: xAxis,
        yAxis: yAxisEarnings,
        valueYField: "earnings",
        valueXField: "date",
        stroke: am5.color("#7922D8"),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY} Earnings",
        }),
      })
    );

    earningsSeries.bullets.push(() => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: earningsSeries.get("fill"),
      });
      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    chart.set(
      "scrollbarX",
      am5.Scrollbar.new(root, {
        orientation: "horizontal",
      })
    );

    ordersSeries.data.setAll(formattedData);
    earningsSeries.data.setAll(formattedData);

    ordersSeries.appear(1000);
    earningsSeries.appear(1000);
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [chartData]);

  return (
    <div
      id="companyLineChartDiv"
      style={{ width: "100%", height: chartHeight || "400px" }}
    />
  );
};

export default CompanyLineChart;
